// Core imports
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useTheme from '../../../utils/hooks/useTheme';

// Component imports
import { Button, Col, Form, Input, Row, Select, Space, Switch, Typography } from 'antd';
import Prompt from '../../UI/Prompt';
import { InfoCircleOutlined } from '@ant-design/icons';

// Misc imports
import { INTEGRATION_CATEGORY, INTEGRATION_TYPE, PLUGIN_CATEGORY } from '../../../constants/integrations';
import { getCurrentQueryState } from '../../../store/utils/getCurrentQueryState';
import { useFetchBotsQuery, useUpdateBotMutation } from '../../../store/bots/botsApi';
import { OPEN_AI_EMBEDDING_MODELS, GEMINI_AI_EMBEDDING_MODELS } from '../../../constants/main';
import CustomInputNumber from '../Fields/CustomInputNumber';
import { useFetchIntegrationsQuery } from '../../../store/integrations/integrationsApi';
import { API_TAGS } from '../../../constants/api';

const PluginSemanticSearchNodeForm = ({
  form,
  onSuccess,
  onCanSubmitChange,
  onLoadingChange,
  integrationType
}) => {

  const { orgId, tenantId } = useParams();
  const { t } = useTranslation();
  const { formProps } = useTheme();
  const { data: fetchedBot } = getCurrentQueryState('fetchBot', { orgId, tenantId });
  const { data: bots } = useFetchBotsQuery({ orgId, urlParams: { includePlugins: true } }, { skip: !orgId || tenantId });
  const bot = useMemo(() => fetchedBot || (bots?.items || []).find(bot => bot.id === form.getFieldValue('bot')), [fetchedBot, bots, form.getFieldValue('bot')]);

  const { data: integrationsData, isFetching: isFetchingIntegrations } = useFetchIntegrationsQuery({ orgId, urlParams: { type: integrationType } }, { skip: !orgId });

  const integrationOptions = useMemo(() => {
    return (integrationsData && integrationsData?.items?.map(int => ({ label: int.name, value: int.id }))) ?? [];
  }, [integrationsData]);

  const semanticSearchPlugin = useMemo(() => (bot?.plugins || []).find(plugin => plugin.integration.category === INTEGRATION_CATEGORY.LLM && plugin.category === PLUGIN_CATEGORY.LLM_SEMANTIC_SEARCH_NODE), [bot]);
  const [, updateState] = useState();
  const [updateBot, { isLoading: isUpdatingBot }] = useUpdateBotMutation();
  const [canSubmit, setCanSubmit] = useState(false);
  const [settingsChanged, setSettingsChanged] = useState(!semanticSearchPlugin);
  const [localModels, setLocalModels] = useState([]);

  const isGeminiIntegration = (semanticSearchPlugin?.integration?.type) === INTEGRATION_TYPE.GEMINI_AI_LLM_SEMANTIC_SEARCH_NODE;
  const isAzureIntegration = (semanticSearchPlugin?.integration?.type) === INTEGRATION_TYPE.AZURE_AI_LLM_SEMANTIC_SEARCH_NODE;

  const currentModelListToBeUsed = useMemo(() => {
    return isGeminiIntegration ? GEMINI_AI_EMBEDDING_MODELS : OPEN_AI_EMBEDDING_MODELS;
  }, [isGeminiIntegration]);

  useEffect(() => {
    form.resetFields();
  }, [fetchedBot?.id]);

  useEffect(() => {
    setLocalModels(Object.values(currentModelListToBeUsed));
  }, []);

  const updateModels = (newModelName) => {
    if (newModelName && Object.values(currentModelListToBeUsed).filter(model => model === newModelName).length === 0) {
      setLocalModels([...new Set([...Object.values(currentModelListToBeUsed), newModelName])]);
    }
  };

  useEffect(() => {
    // Reset the form fields when the bot changes (except for the bot field itself)
    form.resetFields(Object.keys(form.getFieldsValue()).filter(field => field !== 'bot'));
  }, [bot?.id]);

  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(isUpdatingBot);
    }
  }, [isUpdatingBot]);

  const updateCanSubmit = async () => {
    try {
      await form.validateFields();
      setCanSubmit(true);
      if (onCanSubmitChange) {
        onCanSubmitChange(true);
      }
    } catch (e) {
      const formHasErrors = e.errorFields?.length > 0;
      setCanSubmit(!formHasErrors);
      if (onCanSubmitChange) {
        onCanSubmitChange(!formHasErrors);
      }
    }
  };

  const onSubmit = async (values) => {
    let isSubmitSuccessful = true;

    if (settingsChanged) {
      const { integrationId, ...settingsValues } = values;
      let newPlugins = [...(bot?.plugins || [])];

      const existingPluginIndex = newPlugins.findIndex(
        p => p.category === PLUGIN_CATEGORY.LLM_SEMANTIC_SEARCH_NODE
          && (p.integration?.type === integrationType || p.integrationId === integrationId)
      );

      const newPluginData = {
        integrationId: integrationId,
        category: PLUGIN_CATEGORY.LLM_SEMANTIC_SEARCH_NODE,
        settings: existingPluginIndex >= 0
          ? { ...newPlugins[existingPluginIndex].settings, ...settingsValues }
          : settingsValues
      };

      if (existingPluginIndex >= 0) {
        newPlugins[existingPluginIndex] = newPluginData;
      } else {
        newPlugins.push(newPluginData);
      }

      const safePlugins = newPlugins.map(p => ({
        integrationId: p.integrationId || p.integration?.id,
        category: p.category,
        settings: p.settings
      }));

      isSubmitSuccessful = await updateBot({
        orgId,
        tenantId: bot?.id,
        data: { plugins: safePlugins },
        resourcesToInvalidate: [API_TAGS.NLP_PIPELINES]
      }).unwrap();
    }

    if (isSubmitSuccessful) {
      setCanSubmit(false);
      setSettingsChanged(false);
      if (onSuccess) {
        onSuccess(values);
      }
    }
  };

  return (
    <Fragment>
      <Form
        form={form}
        onFinish={onSubmit}
        layout="vertical"
        onValuesChange={(changedValue, allFields) => {
          const fieldIsBot = changedValue.bot;
          if (!fieldIsBot) {
            setSettingsChanged(true);
            (async () => {
              await updateCanSubmit();
            })();
          }
          updateState(allFields);
        }}
        initialValues={{
          integrationId: semanticSearchPlugin?.integration?.id || null,
          exactMatch: semanticSearchPlugin?.settings?.exactMatch || false,
          normalizeCorpus: semanticSearchPlugin?.settings?.normalizeCorpus || false,
          maxInputTokens: semanticSearchPlugin?.settings?.maxInputTokens || 20000,
          visitNeighbours: semanticSearchPlugin?.settings?.visitNeighbours || 128,
          model: semanticSearchPlugin?.settings?.model || null,
          nlpPipelineId: semanticSearchPlugin?.settings?.nlpPipelineId || '',
          nlpAccessToken: semanticSearchPlugin?.settings?.nlpAccessToken || ''
        }}
      >
        <Fragment>
          <Row gutter={formProps.rowGutter} className={formProps.flexDirection}>
            <Col xs={formProps.mainColSpan}>
              <Typography.Text>{t('Integration Configuration')}</Typography.Text>
              <Typography.Paragraph className="fs-sm" type="secondary">
                {t('Select which available integration this agent plugin will use. Changing the integration does not cause downtime for the plugin.')}
              </Typography.Paragraph>
            </Col>
            <Col xs={formProps.secondaryColSpan}>
              <Form.Item
                name="integrationId"
                label={t('Select Integration')}
                rules={[{ required: true, message: t('Please select an integration.') }]}
              >
                <Select
                  placeholder={t('Select Integration')}
                  loading={isFetchingIntegrations}
                  options={integrationOptions}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={formProps.rowGutter} className={formProps.flexDirection}>
            <Col xs={formProps.mainColSpan}>
              <Typography.Text>
                {t('Semantic Search Configuration')}
              </Typography.Text>
              <Typography.Paragraph className="fs-sm" type="secondary">
                {t('Configure the settings for the retrieval phase of RAG(e.g top-k)')}
              </Typography.Paragraph>
            </Col>
            <Col xs={formProps.secondaryColSpan}>
              <Space direction="vertical" className="w-100 gap-4">

                <Form.Item
                  required
                  name={['model']}
                  rules={[
                    {
                      required: true,
                      message: t('Field cannot be blank.')
                    }]}
                  label={isAzureIntegration ? t('Deployment') : t('Model')}
                  className="mb-0"
                >
                  <Select
                    showSearch
                    onSearch={updateModels}
                    onBlur={(e) => updateModels(e.target.value)}
                    placeholder={t('Type / Select model')}
                    getPopupContainer={triggerNode => triggerNode.parentElement}
                    options={(localModels || [])
                      .map((element) => {
                        return {
                          value: element,
                          label: element
                        };
                      })
                    // Keep only unique values
                      .filter((elem, index, self) => self.findIndex((t) => {
                        return (t.value === elem.value);
                      }) === index)
                    }
                  />
                </Form.Item>
                <Form.Item
                  label={t('Normalize Corpus')}
                  name={'normalizeCorpus'}
                  className="mb-0"
                  valuePropName="checked"
                  tooltip={{
                    icon: <InfoCircleOutlined />,
                    title: (
                      <Typography.Text type="secondary">{t('Normalize corpus when indexing.')}</Typography.Text>
                    )
                  }}
                >
                  <Switch />
                </Form.Item>

                <Form.Item
                  label={t('Max Input Tokens')}
                  name={'maxInputTokens'}
                  className="mb-0"
                  tooltip={{
                    icon: <InfoCircleOutlined />,
                    title: (
                      <Typography.Text type="secondary">{t('Limit results by total tokens (when set, has priority over max_results).')}</Typography.Text>
                    )
                  }}
                >
                  <CustomInputNumber defaultValue={0} min={0} max={100000} step={500} style={{ width: '80px' }}/>
                </Form.Item>

                <Form.Item
                  label={t('Exact Match')}
                  name={'exactMatch'}
                  className="mb-0"
                  valuePropName="checked"
                  tooltip={{
                    icon: <InfoCircleOutlined />,
                    title: (
                      <Typography.Text type="secondary">{t('Prefer exact match when searching (results in slower performance).')}</Typography.Text>
                    )
                  }}
                >
                  <Switch />
                </Form.Item>

                <Form.Item
                  label={t('Visit Neighbors')}
                  name={'visitNeighbours'}
                  className="mb-0"
                  tooltip={{
                    icon: <InfoCircleOutlined />,
                    title: (
                      <Typography.Text type="secondary">{t('Number of neighbors to visit during semantic search (higher value leads to improved accuracy but results in slower performance).')}</Typography.Text>
                    )
                  }}
                >
                  <CustomInputNumber defaultValue={128} min={1} />
                </Form.Item>

                <Form.Item
                  label={t('Pipeline ID')}
                  name={'nlpPipelineId'}
                  hidden={!semanticSearchPlugin}
                  className="mb-0"
                >
                  <Input readOnly />
                </Form.Item>

                <Form.Item
                  label={t('Access Token')}
                  name={'nlpAccessToken'}
                  hidden={!semanticSearchPlugin}
                >
                  <Input.TextArea
                    autoSize={{ minRows: 6, maxRows: 6 }}
                    rows={4}
                    readOnly
                  />
                </Form.Item>
              </Space>
            </Col>
          </Row>
        </Fragment>

      </Form>

      <Prompt
        when={canSubmit}
        onConfirm={() => form.submit()}
        okText={t('Save Changes')}
        cancelText={t('Quit without saving')}
        title={t('Save Changes?')}
        message={t('Your unsaved changes will be lost. Save changes before closing?')}
      />

      {!onSuccess ? (
        <Row justify="end" className="mt-4">
          <Col>
            <Button
              type="primary"
              size="large"
              onClick={() => form.submit()}
              disabled={!canSubmit}
              loading={isUpdatingBot}
            >
              {t('Save Changes')}
            </Button>
          </Col>
        </Row>
      ) : null}
    </Fragment>
  );
};

PluginSemanticSearchNodeForm.propTypes = {
  form: PropTypes.object.isRequired,
  onSuccess: PropTypes.func,
  onCanSubmitChange: PropTypes.func,
  onLoadingChange: PropTypes.func,
  integrationType: PropTypes.string
};

export default PluginSemanticSearchNodeForm;
